import React, { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from '@vibesites/theme-prestige/components/_galleryShared';

// ─── Slot group definitions ────────────────────────────────────────────────────
// Four square images forming a social-style image strip. Each defaults to the
// next photo in the property's gallery via its sourceBinding.

const image1Def: SlotGroupDefinition = {
  label: 'Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image2Def: SlotGroupDefinition = {
  label: 'Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image3Def: SlotGroupDefinition = {
  label: 'Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image4Def: SlotGroupDefinition = {
  label: 'Image 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ─── Presentational tile ────────────────────────────────────────────────────────

function ImageTile({
  src,
  alt,
  onOpen,
}: {
  src: string | null | undefined;
  alt: string;
  onOpen?: () => void;
}) {
  if (!src) {
    return <div className="aspect-square overflow-hidden bg-[var(--vibe-color-surface)]" />;
  }
  return (
    <div
      className="aspect-square overflow-hidden cursor-pointer group"
      onClick={onOpen}
    >
      <VibesitesImage
        url={src}
        alt={alt || 'Community photo'}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
      />
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InstagramSection() {
  const image1 = useSlotGroup('instagramsection.image1', image1Def);
  const image2 = useSlotGroup('instagramsection.image2', image2Def);
  const image3 = useSlotGroup('instagramsection.image3', image3Def);
  const image4 = useSlotGroup('instagramsection.image4', image4Def);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Build the lightbox image list from whichever slots have an image, and
  // record each populated slot's position within that list so a click opens
  // the lightbox on the right photo.
  const slots = [image1, image2, image3, image4];
  const lightboxImages: GalleryImage[] = [];
  const lightboxIndex = slots.map((slot) => {
    if (!slot.image) return null;
    const idx = lightboxImages.length;
    lightboxImages.push({ image: slot.image, alt_text: slot.alt || 'Community photo' });
    return idx;
  });

  const open = (i: number | null) => () => {
    if (i !== null) setSelectedIndex(i);
  };

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-secondary)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Subtitle */}
        <RichTextContent
          path="instagramsection.subtitle"
          label="Subtitle"
          defaultValue="<p>Let's Be Friends</p>"
          as="p"
          className="font-body text-sm sm:text-base text-[var(--vibe-color-text-on-secondary)] mb-6 tracking-wide uppercase"
        />

        {/* Title */}
        <RichTextContent
          path="instagramsection.title"
          label="Title"
          defaultValue="<p>Follow Us On Social</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text-on-secondary)] mb-4 leading-tight uppercase"
        />

        {/* Accent divider */}
        <div className="w-20 h-1 mx-auto mb-12 rounded-full bg-[var(--vibe-color-primary)]" />

        {/* Four square images */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          <EditableSlotGroup groupPath="instagramsection.image1" definition={image1Def}>
            <ImageTile src={image1.image} alt={image1.alt} onOpen={open(lightboxIndex[0])} />
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="instagramsection.image2" definition={image2Def}>
            <ImageTile src={image2.image} alt={image2.alt} onOpen={open(lightboxIndex[1])} />
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="instagramsection.image3" definition={image3Def}>
            <ImageTile src={image3.image} alt={image3.alt} onOpen={open(lightboxIndex[2])} />
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="instagramsection.image4" definition={image4Def}>
            <ImageTile src={image4.image} alt={image4.alt} onOpen={open(lightboxIndex[3])} />
          </EditableSlotGroup>
        </div>
      </div>

      {/* Lightbox gallery, opened on image click */}
      <GalleryLightbox
        images={lightboxImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />
      <GallerySharedStyles />
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'InstagramSection',
  displayName: 'Instagram Section',
  tokens: {
    'instagramsection.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Let\'s Be Friends</p>',
    },
    'instagramsection.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Follow Us On Social</p>',
    },
  },
  slotGroups: {
    'instagramsection.image1': image1Def,
    'instagramsection.image2': image2Def,
    'instagramsection.image3': image3Def,
    'instagramsection.image4': image4Def,
  },
};

(InstagramSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InstagramSection;
